/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';

import {doPreview} from '../../base/nude/creative-preview-runner';
import {collateCreativePreviewLineItemData} from './preview-request-data';
import {
  PREVIEW_LANGUAGE_OPTIONS,
  resolvePreviewLanguageCode,
  shouldShowPreviewLanguageSelector
} from './preview-language';
import {CreativePreviewResource} from '../../../components/api/resources/resource/creative-preview.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {WINDOW} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-creative-direct',
  template: `
    <p class="text-center" *ngIf="!resolved">
      <span class="fa fa-2x fa-cog fa-spin"></span>
    </p>
    <div class="small text-right" *ngIf="resolved && showLanguageSelector">
      <p>
        <label class="simpleLabel">
          Language
          <select class="form-control input-sm" style="display: inline-block; width: auto;"
                  [(ngModel)]="previewLanguage"
                  (ngModelChange)="onPreviewLanguageChange()"
                  name="creativeDirectPreviewLanguage">
            <option *ngFor="let option of previewLanguageOptions" [ngValue]="option.value">{{ option.label }}</option>
          </select>
        </label>
      </p>
    </div>
    <!-- Target must stay in the DOM (not behind *ngIf) for the ad server to render into. -->
    <div [id]="targetId"></div>
  `
})
export class CreativeDirectComponent implements OnInit, OnChanges {
  @Input() creative: any;

  resolved = false;
  showLanguageSelector = false;
  previewLanguage = 'browser';
  readonly previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  targetId = '';
  errorMessage: any;

  constructor(
    @Inject(WINDOW) private readonly window: any,
    @Inject(CreativePreviewResource) private readonly CreativePreview: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.initPreview();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.creative && !changes.creative.firstChange) {
      this.initPreview();
    }
  }

  onPreviewLanguageChange(): void {
    this.previewIt();
  }

  private initPreview(): void {
    if (!this.creative) {
      return;
    }
    const creativeId = this.creative.id || this.creative.creativeId;
    this.targetId = 'target-' + creativeId;
    this.showLanguageSelector = shouldShowPreviewLanguageSelector(this.creative);
    this.setupRedrawHook();
    this.previewIt();
  }

  private setupRedrawHook(): void {
    if (!_.isObject(this.creative?.redrawHook)) {
      return;
    }
    this.creative.redrawHook.triggerPreview = (c?: any) => {
      if (c) {
        this.creative.allowScript = c.allowScript;
      }
      this.previewIt();
    };
  }

  private selectedLanguageCode(): string | null {
    return this.showLanguageSelector
      ? resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator)
      : null;
  }

  private previewIt(): void {
    if (!this.creative) {
      return;
    }
    this.resolved = false;
    this.cdr.markForCheck();
    const previewData: any = {
      networkId: this.LocalNetworkProfile.getNetworkTag(),
      creativeId: this.creative.id || this.creative.creativeId,
      creativeWidth: this.creative.width || this.creative.creativeWidth,
      creativeHeight: this.creative.height || this.creative.creativeHeight,
      allowScript: this.creative.allowScript,
      clearTarget: _.isObject(this.creative.redrawHook),
      impressionCallback: () => {
        this.resolved = true;
        this.cdr.markForCheck();
      },
      dataLang: this.selectedLanguageCode()
    };
    _.assign(previewData, collateCreativePreviewLineItemData(this.creative));

    doPreview(
      this.window,
      null,
      null,
      null,
      () => this.cdr.markForCheck(),
      previewData,
      false,
      this.CreativePreview,
      this,
      this.targetId
    );
  }
}
